/**
 * Mayor brief — one screen the mayor of Chonburi can read in 30 seconds and
 * forward to a LINE group.
 *
 * Pure: it only reshapes feeds the dashboard already has, so every number
 * keeps its source and age, and a feed that is missing says "no data" instead
 * of reading as calm. Thai is written first because that is the language the
 * brief is forwarded in; the English line is a gloss of the same sentence,
 * never a separate or weaker claim.
 *
 * Four tiles, ordered by what a coastal municipality is actually judged on:
 * air, rain and water, sea state, and the civic incident load.
 */

import { sourceName, type FallbackTier } from "@chonburi/shared";
import { alertLevel } from "./water";

/** Severity ladder. `unknown` is not a severity — it means "no usable reading". */
export type BriefLevel = "good" | "watch" | "alert" | "critical" | "unknown";

export type BriefKey = "air" | "rain" | "sea" | "incidents";

/** Lens the tile's evidence lives on. Matches `LensId` in map/presets. */
export type BriefEvidenceLens = "environment" | "maritime" | "operations" | "safety";

export interface BriefTile {
  key: BriefKey;
  titleTh: string;
  titleEn: string;
  level: BriefLevel;
  /**
   * Subject-specific level label, used instead of the bare severity word.
   *
   * The water tile carries two questions — is it flooding, and is the stored
   * water running out — and only one chip can sit on it. A bare "Normal" over a
   * reservoir at 6 % reads as if nothing in the tile needs attention, which is
   * the one thing this brief must never do. Naming the subject keeps the chip
   * true to what it actually measured.
   */
  levelLabel?: { th: string; en: string };
  /** Which subject set the level, when the tile covers more than one. */
  driver?: "rain" | "storage";
  /** The Thai statement — plain, specific, honest about its own limits. */
  th: string;
  /** The English gloss of the same statement. */
  en: string;
  source: string;
  summaryTh?: string;
  summaryEn?: string;
  /** ISO timestamp of the observation the line is built on; null when there is none. */
  observedAt: string | null;
  evidence: BriefEvidenceLens;
  /**
   * The one figure a compact cell can hold.
   *
   * The brief's sentences carry every disclosure and are far too long for a
   * strip of four cells. A strip needs one number — and the cell keeps the
   * source and the reference time beside it, so the number is never free of
   * its provenance.
   */
  figure?: { th: string; en: string };
}

export const LEVEL_LABEL: Record<BriefLevel, { th: string; en: string }> = {
  good: { th: "ปกติ", en: "Normal" },
  watch: { th: "เฝ้าระวัง", en: "Watch" },
  alert: { th: "แจ้งเตือน", en: "Alert" },
  critical: { th: "วิกฤต", en: "Critical" },
  unknown: { th: "ไม่มีข้อมูล", en: "No data" },
};

/** LINE markers. Readable in a Thai group chat without a legend. */
const MARK: Record<BriefLevel, string> = {
  good: "🟢",
  watch: "🟡",
  alert: "🟠",
  critical: "🔴",
  unknown: "⚪",
};

const LEVEL_RANK: Record<BriefLevel, number> = {
  good: 0,
  watch: 1,
  alert: 2,
  critical: 3,
  // `unknown` is not on the ladder — worseLevel resolves it before ranking.
  unknown: -1,
};

/**
 * Combine two levels. `unknown` loses to any real reading, because a missing
 * feed is not evidence of calm — the tile that knows something wins.
 */
export function worseLevel(a: BriefLevel, b: BriefLevel): BriefLevel {
  if (a === "unknown") return b;
  if (b === "unknown") return a;
  return LEVEL_RANK[a] >= LEVEL_RANK[b] ? a : b;
}

/**
 * Is an observation current enough to stand in a briefing?
 * A future timestamp (clock skew) is rejected rather than trusted.
 */
export function isBriefObservationCurrent(
  observedAt: string | null,
  now = new Date(),
  maxAgeHours = 3,
): boolean {
  const age = now.getTime() - Date.parse(observedAt ?? "");
  return Number.isFinite(age) && age >= -5 * 60_000 && age <= maxAgeHours * 3_600_000;
}

const TH_MONTHS = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];

/** "2026-10-21" → "21 ต.ค. 2569" (Buddhist era). */
export function thaiDate(isoDate: string): string {
  const [y, m, d] = isoDate.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d || m < 1 || m > 12) return "—";
  return `${d} ${TH_MONTHS[m - 1]} ${y + 543}`;
}

/** "23:15 น." in Thai time (ICT). */
export function ictTime(now: Date): string {
  const ict = new Date(now.getTime() + 7 * 3_600_000);
  const hh = String(ict.getUTCHours()).padStart(2, "0");
  const mm = String(ict.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} น.`;
}

// ── Input shapes ───────────────────────────────────────────────────────────

export interface AirBriefInput {
  aqi: number | null;
  pm25: number | null;
  /** Adapter's own AQI category, when it published one. */
  category: string | null;
  station: string | null;
  distanceKm?: number;
  observedAt: string | null;
  source: string;
  /**
   * Where the number came from. A government station reading is `measured`;
   * a forecast grid cell is `model` and must never be read as a sensor.
   */
  kind: "measured" | "model";
  /**
   * Which authority produced the AQI number. The Thai PCD scale and the US
   * EPA scale share breakpoints but are different authorities, so the brief
   * names the one it is quoting.
   */
  scale: "thai-pcd" | "us-epa";
}

export interface RainBriefInput {
  /** Modelled precipitation amount over the first 15-minute forecast interval. */
  nowMm: number;
  /** Cumulative mm forecast across the next 2 hours — a model, not a measurement. */
  total2hMm: number;
  /** Largest single 15-minute interval in the forecast. */
  peakMm: number;
  /** Minutes to the first interval with ≥ 0.5 mm, or null when dry. */
  minutesToSignificant: number | null;
  intensity: "dry" | "light" | "moderate" | "heavy";
  observedAt: string | null;
  source: string;
}

export interface WaterBriefInput {
  /**
   * The reservoir that runs dry first — usable water above dead storage ÷
   * today's net depletion. Null when no dam is drawing down.
   */
  tightest: { name: string; daysRemaining: number; capacityPct: number | null } | null;
  /**
   * True when the flows needed to judge the reservoirs are not published. A
   * missing answer is not a dam that is fine.
   */
  flowsUnknown: boolean;
  /**
   * When the storage was observed — the report's own date, not the moment
   * the dashboard fetched it.
   */
  observedAt: string | null;
  source: string;
}

export interface SeaBriefInput {
  waveHeightM: number | null;
  windKmh: number | null;
  windGustsKmh: number | null;
  /** null when the upstream did not publish a verdict. */
  smallBoatBelowThreshold: boolean | null;
  ferryBelowThreshold: boolean | null;
  /** Maximum forecast wave height over the next 24 h; not storm surge. */
  peakWaveHeightNext24hM: number | null;
  tideHeightM: number | null;
  tideRising: boolean | null;
  springTide: boolean | null;
  observedAt: string | null;
  source: string;
}

export interface IncidentBriefInput {
  coverage?: { available: number; total: number };
  /** Incidents still open. */
  open: number;
  critical: number;
  high: number;
  /** Open incidents whose category is flooding. */
  flooding: number;
  observedAt: string | null;
  source: string;
}

export interface FeedHealthInput {
  scope?: "brief" | "dashboard";
  scenario?: number;
  inputs?: BriefFeedState[];
  live: number;
  total: number;
  degraded: number;
  down: number;
  /**
   * Feeds that have not answered yet. Counted apart from "degraded" and
   * "down" so a dashboard that is still loading does not read as a broken
   * one — a missing answer is not a failed feed.
   */
  loading?: number;
}

export interface MayorBriefInputs {
  now: Date;
  air: AirBriefInput | null;
  rain: RainBriefInput | null;
  water: WaterBriefInput | null;
  sea: SeaBriefInput | null;
  incidents: IncidentBriefInput | null;
  feeds: FeedHealthInput;
}

export interface BriefFeedState {
  name: string;
  tier: FallbackTier | "loading";
  kind: "observation" | "forecast" | "reference";
}

/** Every tier is accounted for; this census covers only these named inputs. */
export function summarizeBriefFeeds(inputs: BriefFeedState[]): FeedHealthInput {
  return {
    scope: "brief", inputs, total: inputs.length,
    live: inputs.filter(f => f.tier === "live").length,
    degraded: inputs.filter(f => ["cache", "database", "reference"].includes(f.tier)).length,
    scenario: inputs.filter(f => f.tier === "scenario").length,
    down: inputs.filter(f => f.tier === "unavailable").length,
    loading: inputs.filter(f => f.tier === "loading").length,
  };
}

// ── Band helpers ───────────────────────────────────────────────────────────

export interface AqiBand {
  level: BriefLevel;
  th: string;
  en: string;
}

/**
 * Apply the published authority's bands; the two AQI scales differ.
 */
export function aqiBand(aqi: number, scale: AirBriefInput["scale"] = "us-epa"): AqiBand {
  // PCD's five bands are not the US EPA six-band scale.
  // PCD: https://www.pcd.go.th/wp-content/uploads/2025/08/pcdnew-2025-08-01_07-12-19_226372.pdf
  if (scale === "thai-pcd") {
    if (aqi <= 25) return { level: "good", th: "ดีมาก", en: "Very good" };
    if (aqi <= 50) return { level: "good", th: "ดี", en: "Good" };
    if (aqi <= 100) return { level: "watch", th: "ปานกลาง", en: "Moderate" };
    if (aqi <= 200) return { level: "alert", th: "เริ่มมีผลกระทบต่อสุขภาพ", en: "Starts affecting health" };
    return { level: "critical", th: "มีผลกระทบต่อสุขภาพ", en: "Affects health" };
  }
  if (aqi <= 50) return { level: "good", th: "ดี", en: "Good" };
  if (aqi <= 100) return { level: "watch", th: "ปานกลาง", en: "Moderate" };
  if (aqi <= 150) return { level: "alert", th: "เริ่มมีผลกระทบต่อสุขภาพ", en: "Starting to affect health" };
  if (aqi <= 200) return { level: "critical", th: "มีผลกระทบต่อสุขภาพ", en: "Affecting health" };
  return { level: "critical", th: "มีผลกระทบต่อสุขภาพอย่างรุนแรง", en: "Severely affecting health" };
}

const CATEGORY_LEVEL: Record<string, BriefLevel> = {
  good: "good",
  moderate: "watch",
  "unhealthy-sg": "alert",
  unhealthy: "critical",
  "very-unhealthy": "critical",
  hazardous: "critical",
};

/**
 * The level an AQI reading implies.
 *
 * The AQI number is quoted on its own authority, so it is the primary
 * signal. The adapter's `category` only stands in when there is no AQI
 * number — for the PCD feed that matters, because its category is derived
 * from PM2.5 on US EPA breakpoints, which is a *different axis* from the
 * PCD AQI it publishes alongside. Preferring the category there would mix
 * two authorities in one sentence.
 */
export function airLevel(aqi: number | null, category: string | null, scale: AirBriefInput["scale"] = "us-epa"): BriefLevel {
  if (aqi != null && Number.isFinite(aqi)) return aqiBand(aqi, scale).level;
  if (category) {
    const mapped = CATEGORY_LEVEL[category];
    if (mapped) return mapped;
  }
  return "unknown";
}

// ── Tiles ───────────────────────────────────────────────────────────────────

/**
 * The authority behind each tile, named the same way whether or not the feed
 * answered.
 *
 * A tile that has no reading still says where the reading would come from, so
 * that name has to match the one the tile prints when it DOES have one.
 * "Air4Thai · PCD" beside a live "PCD Air4Thai" is the same authority under
 * two labels, and a brief that renames its own sources is a brief nobody
 * trusts. These go through `sourceName` for that reason.
 */
const SRC_AIR = sourceName("air4thai-pcd");
const SRC_RAIN = sourceName("open-meteo-minutely");
const SRC_WATER = sourceName("rid-public-reservoirs");
const SRC_SEA = sourceName("open-meteo-marine");
const SRC_TIDE = sourceName("harmonic-tide-si-racha");
const SRC_INCIDENTS = sourceName("traffy-fondue");
const SRC_IFIC = sourceName("itic-longdo");

/**
 * Name a tile's source, whichever shape it arrives in.
 *
 * A source is either one feed ("air4thai:o61") or several joined by " · "
 * ("traffy-fondue · itic-longdo"). Naming each part keeps the join intact and
 * leaves a name that is already an authority untouched.
 *
 * The lib owns this rather than the component, so an adapter id cannot reach
 * the forwarded text no matter who builds the inputs.
 */
function nameSource(source: string | undefined | null): string {
  if (!source) return "";
  return source
    .split(" · ")
    .map((part) => sourceName(part.trim()))
    .filter(Boolean)
    .join(" · ");
}

const AIR_CAVEAT_MEASURED_TH = "ค่าจากสถานีวัดจริง เป็นค่าคัดกรอง ณ เวลาที่วัด ไม่ใช่ค่าสัมผัสเฉลี่ย 24 ชม.";
const AIR_CAVEAT_MEASURED_EN = "a real station reading — a screening value at observation time, not a 24-hour average exposure";
const AIR_CAVEAT_MODEL_TH = "ค่าจากแบบจำลองการพยากรณ์ของตารางภูมิอากาศ ไม่ใช่ค่าที่วัดจากสถานี";
const AIR_CAVEAT_MODEL_EN = "a forecast-model value for the grid cell, not a station measurement";

const SCALE_LABEL: Record<AirBriefInput["scale"], string> = {
  "thai-pcd": "สเกลไทย PCD",
  "us-epa": "US EPA scale",
};

function airTile(i: MayorBriefInputs): BriefTile {
  const a = i.air;
  const base = {
    key: "air" as const,
    titleTh: "คุณภาพอากาศ",
    titleEn: "Air quality",
    source: nameSource(a?.source) || SRC_AIR,
    observedAt: null as string | null,
    evidence: "environment" as const,
  };

  if (!a || !isBriefObservationCurrent(a.observedAt, i.now, a.kind === "measured" ? 1.5 : 3)) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีข้อมูลคุณภาพอากาศที่เป็นปัจจุบัน",
      en: "No current air-quality reading",
    };
  }

  const level = airLevel(a.aqi, a.category, a.scale);
  const band = a.aqi != null ? aqiBand(a.aqi, a.scale) : null;

  const thParts: string[] = [];
  const enParts: string[] = [];
  if (a.pm25 != null) {
    thParts.push(`PM2.5 ${a.pm25} มคก./ลบ.ม.`);
    enParts.push(`PM2.5 ${a.pm25} µg/m³`);
  }
  if (a.aqi != null) {
    thParts.push(`AQI ${a.aqi}${band ? ` (${band.th})` : ""}`);
    enParts.push(`AQI ${a.aqi}${band ? ` (${band.en})` : ""}`);
  }

  if (!thParts.length) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีตัวเลขคุณภาพอากาศที่ใช้ได้จากสถานีที่วัดล่าสุด",
      en: "The latest station returned no usable air-quality value",
    };
  }

  const stationTh = a.station ? `สถานี ${a.station}${a.distanceKm != null ? ` ห่างจุดศูนย์กลางเมือง ${a.distanceKm.toFixed(1)} กม.` : ""}` : "ตารางแบบจำลอง";
  const stationEn = a.station ? `${a.station}${a.distanceKm != null ? `, ${a.distanceKm.toFixed(1)} km from town centre` : ""}` : "forecast grid";
  const caveatTh = a.kind === "model" ? AIR_CAVEAT_MODEL_TH : AIR_CAVEAT_MEASURED_TH;
  const caveatEn = a.kind === "model" ? AIR_CAVEAT_MODEL_EN : AIR_CAVEAT_MEASURED_EN;

  const th = `${thParts.join(" · ")} (${SCALE_LABEL[a.scale]}) — ${stationTh} · ${caveatTh}`;
  const en = `${enParts.join(" · ")} (${SCALE_LABEL[a.scale]}) — ${stationEn} · ${caveatEn}`;

  return {
    ...base,
    level,
    th, en,
    summaryTh: `${thParts.join(" · ")} · ${a.kind === "model" ? "แบบจำลอง" : `สถานี ${a.station}${a.distanceKm != null ? ` · ${a.distanceKm.toFixed(1)} กม. จากศูนย์กลางเมือง` : ""}`}`,
    summaryEn: `${enParts.join(" · ")} · ${a.scale === "thai-pcd" ? "Thai PCD" : "US EPA"} · ${a.kind}`,
    figure:
      a.aqi != null && a.pm25 != null
        ? { th: `AQI ${a.aqi} · PM2.5 ${a.pm25}`, en: `AQI ${a.aqi} · PM2.5 ${a.pm25}` }
        : a.aqi != null
          ? { th: `AQI ${a.aqi}`, en: `AQI ${a.aqi}` }
          : a.pm25 != null
            ? { th: `PM2.5 ${a.pm25} มคก./ลบ.ม.`, en: `PM2.5 ${a.pm25} µg/m³` }
            : undefined,
    observedAt: a.observedAt,
  };
}

/**
 * Storage severity, from how long the water lasts — not from how full the dam is.
 *
 * Fill percentage is a poor trigger. ชากนอก holds 0.44 MCM of a 7.03 MCM
 * conservation capacity (6 %), but its own 1-November reference volume was
 * 1.1 MCM (16 %): a shallow basin that sits low even at the end of the monsoon.
 * A fill band would fire every dry season for a dam behaving normally, and a
 * brief that cries wolf is a brief nobody reads.
 *
 * The horizon separates the real case, and it is measured net of inflow: a
 * dam that is being refilled has no exhaustion horizon at all, and one that
 * reads "seven days" while it takes in more than it gives out is an
 * emergency invented by the arithmetic. The bands are the dashboard's own
 * (`lib/water.ts` alertLevel), so the brief and the water panel cannot
 * disagree about the same dam.
 *
 * A missing horizon is "unknown", not "good": the feed answered, but it did
 * not answer the question that decides the level.
 */
export function storageLevel(daysRemaining: number | null): BriefLevel {
  if (daysRemaining == null || !Number.isFinite(daysRemaining)) return "unknown";
  const level = alertLevel(daysRemaining);
  if (level === "critical") return "critical";
  if (level === "low") return "alert";
  if (level === "watch") return "watch";
  return "good";
}

/** Level words that name the subject, so the chip never covers the wrong one. */
const RAIN_LEVEL_LABEL: Partial<Record<BriefLevel, { th: string; en: string }>> = {
  watch: { th: "เฝ้าระวังฝน", en: "Rain watch" },
  alert: { th: "คาดการณ์ฝนหนัก", en: "Heavy rain forecast" },
  critical: { th: "คาดการณ์ฝนรุนแรง", en: "Severe rain forecast" },
};

/**
 * The chip names the subject and how urgent the supply is.
 *
 * The severity is the supply horizon, never the fill — a shallow basin sits
 * low all dry season, so a fill band cries wolf every October. But the WORD
 * has to match the horizon, not the level: "Storage low" printed above a
 * reservoir at 100 % contradicts the number beside it in the same way a bare
 * "Normal" above a reservoir at 6 % did. The horizon says how long the water
 * lasts; the sentence says how full the dam is.
 */
const STORAGE_LEVEL_LABEL: Partial<Record<BriefLevel, { th: string; en: string }>> = {
  watch: { th: "เฝ้าระวังกักเก็บน้ำ", en: "Storage watch" },
  alert: { th: "กักเก็บน้ำใกล้หมด", en: "Storage nearly exhausted" },
  critical: { th: "กักเก็บน้ำวิกฤต", en: "Storage critical" },
};

function rainTile(i: MayorBriefInputs): BriefTile {
  const r = i.rain;
  const w = i.water;
  const base = {
    key: "rain" as const,
    titleTh: "ฝน น้ำท่วม และกักเก็บน้ำ",
    titleEn: "Rain, flood and storage",
    source: nameSource([r?.source, w?.source].filter(Boolean).join(" · ")) || `${SRC_RAIN} · ${SRC_WATER}`,
    observedAt: null as string | null,
    evidence: "safety" as const,
  };

  const rainCurrent = r != null && isBriefObservationCurrent(r.observedAt, i.now, 1);
  // The storage observation is the report's own date (00:00 ICT), so it is
  // already hours old when it is fresh. Allow today and the previous daily report,
  // never a three-day-old operational storage estimate.
  const waterCurrent = w != null && isBriefObservationCurrent(w.observedAt, i.now, 36);

  if (!rainCurrent && !waterCurrent) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีข้อมูลฝนและสถานะอ่างเก็บน้ำที่เป็นปัจจุบัน",
      en: "No current rain or reservoir reading",
    };
  }

  let floodLevel: BriefLevel = "unknown";
  let storeLevel: BriefLevel = "unknown";
  const thParts: string[] = [];
  const enParts: string[] = [];

  if (rainCurrent && r) {
    const heavy = r.peakMm >= 10 || r.intensity === "heavy";
    const moderate = r.total2hMm >= 5 || r.intensity === "moderate";
    floodLevel = heavy ? "alert" : moderate ? "watch" : "good";

    if (r.nowMm > 0) {
      thParts.push(`คาดการณ์ฝน ${r.nowMm} มม./15 นาที (แบบจำลอง)`);
      enParts.push(`rain forecast ${r.nowMm} mm/15 min (model)`);
    }
    if (r.total2hMm > 0) {
      thParts.push(`คาดการณ์ 2 ชม. ${r.total2hMm} มม. (แบบจำลอง)`);
      enParts.push(`2 h forecast ${r.total2hMm} mm (model)`);
    } else if (r.nowMm === 0) {
      thParts.push("แบบจำลองไม่คาดการณ์ฝนในช่วง 15 นาทีแรก");
      enParts.push("model forecasts no rain in the first 15-minute interval");
    }
    if (r.minutesToSignificant != null && r.minutesToSignificant > 0) {
      thParts.push(`คาดว่ามีฝนสำคัญใน ${r.minutesToSignificant} นาที`);
      enParts.push(`significant rain expected in ${r.minutesToSignificant} min`);
    }
  } else {
    thParts.push("ไม่มีข้อมูลฝนที่เป็นปัจจุบัน");
    enParts.push("no current rain reading");
  }

  if (waterCurrent && w) {
    const tight = w.tightest;
    storeLevel = tight ? storageLevel(tight.daysRemaining) : w.flowsUnknown ? "unknown" : "good";

    // Storage is a dated daily report, not a live gauge. Say which report it
    // is, rather than let a number sit in a sentence that reads as a
    // measurement from right now.
    const stamp = w.observedAt ? ` (${nameSource(w.source)} ${thaiDate(w.observedAt)})` : ` (${nameSource(w.source)})`;
    if (tight) {
      const days = Math.round(tight.daysRemaining);
      const pct = tight.capacityPct != null ? ` ${Math.round(tight.capacityPct)}%` : "";
      thParts.push(`กักเก็บน้ำรายวัน${stamp} · ${tight.name}${pct} ประมาณ ${days} วันตามอัตราน้ำลดสุทธิที่รายงาน`);
      enParts.push(`daily storage${stamp} · ${tight.name}${pct} has a ${days}-day supply estimate at the reported net depletion rate`);
    } else if (w.flowsUnknown) {
      thParts.push(`กักเก็บน้ำรายวัน${stamp} · ไม่ทราบระยะเวลาน้ำที่ใช้ได้`);
      enParts.push(`daily storage${stamp} · supply horizon unknown`);
    } else {
      thParts.push(`กักเก็บน้ำรายวัน${stamp} · ไม่มีอ่างเก็บน้ำที่กำลังน้ำลด`);
      enParts.push(`daily storage${stamp} · no reservoir is drawing down`);
    }
  } else {
    thParts.push("ไม่มีข้อมูลสถานะอ่างเก็บน้ำที่เป็นปัจจุบัน");
    enParts.push("no current reservoir reading");
  }

  if (!thParts.length) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีตัวเลขฝนหรืออ่างเก็บน้ำที่ใช้ได้",
      en: "No usable rain or reservoir value",
    };
  }

  // One tile, two subjects: the worse of the two sets the level, and the chip
  // names the subject it is talking about. A subject with no current reading
  // is stated in the sentence, so the tile never reads calm on a gap.
  const level = worseLevel(floodLevel, storeLevel);
  const driver: "rain" | "storage" =
    LEVEL_RANK[storeLevel] > LEVEL_RANK[floodLevel] ? "storage" : "rain";
  const missing =
    floodLevel === "unknown" && storeLevel !== "unknown"
      ? "rain"
      : storeLevel === "unknown" && floodLevel !== "unknown"
        ? "storage"
        : null;

  let levelLabel: { th: string; en: string } | undefined;
  if (driver === "storage") {
    levelLabel =
      STORAGE_LEVEL_LABEL[level] ??
      (missing === "rain"
        ? { th: "กักเก็บน้ำปกติ (ไม่มีข้อมูลฝน)", en: "Storage normal (no rain reading)" }
        : undefined);
  } else {
    levelLabel =
      RAIN_LEVEL_LABEL[level] ??
      (missing === "storage"
        ? { th: "ฝนปกติ (ไม่มีข้อมูลกักเก็บน้ำ)", en: "Rain normal (no storage reading)" }
        : undefined);
  }

  return {
    ...base,
    level,
    ...(levelLabel ? { levelLabel } : {}),
    driver,
    summaryTh: `${rainCurrent && r ? `ฝนแบบจำลอง 2 ชม. ${r.total2hMm} มม.` : "ฝนไม่มีข้อมูล"} · ${waterCurrent && w?.tightest ? `น้ำรายวันประมาณ ${Math.round(w.tightest.daysRemaining)} วันตามอัตราน้ำลดที่รายงาน` : "ระยะน้ำใช้ไม่ทราบ"}`,
    summaryEn: `${rainCurrent && r ? `2h rain model ${r.total2hMm} mm` : "Rain unknown"} · ${waterCurrent && w?.tightest ? `daily storage ~${Math.round(w.tightest.daysRemaining)} days at reported depletion` : "supply horizon unknown"}`,
    // The figure names the subject that set the level, so the number beside
    // the chip is the number the chip is talking about.
    figure:
      driver === "storage" && w?.tightest
        ? { th: `น้ำใช้ได้ ~${Math.round(w.tightest.daysRemaining)} วัน`, en: `water ~${Math.round(w.tightest.daysRemaining)} days` }
        : r
          ? { th: `ฝน ${r.total2hMm} มม./2 ชม.`, en: `rain ${r.total2hMm} mm/2 h` }
          : undefined,
    th: thParts.join(" · "),
    en: enParts.join(" · "),
    observedAt: rainCurrent ? r?.observedAt ?? null : w?.observedAt ?? null,
  };
}

function seaTile(i: MayorBriefInputs): BriefTile {
  const s = i.sea;
  const base = {
    key: "sea" as const,
    titleTh: "สถานการณ์ทะเลและคลื่น",
    titleEn: "Sea state and tide",
    source: nameSource(s?.source) || `${SRC_SEA} · ${SRC_TIDE}`,
    observedAt: null as string | null,
    evidence: "maritime" as const,
  };

  if (!s || !isBriefObservationCurrent(s.observedAt, i.now, 3)) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีข้อมูลสถานการณ์ทะเลที่เป็นปัจจุบัน",
      en: "No current sea-state reading",
    };
  }

  let level: BriefLevel = "good";
  const thParts: string[] = [];
  const enParts: string[] = [];

  if (s.waveHeightM != null) {
    level = worseLevel(level, s.waveHeightM >= 2.5 ? "alert" : s.waveHeightM >= 1.5 ? "watch" : "good");
    thParts.push(`คลื่นสูง ${s.waveHeightM} ม.`);
    enParts.push(`waves ${s.waveHeightM} m`);
  }
  if (s.windKmh != null) {
    level = worseLevel(level, s.windKmh >= 40 ? "alert" : s.windKmh >= 25 ? "watch" : "good");
    const gust = s.windGustsKmh != null && s.windGustsKmh > s.windKmh ? ` (ลมกระโชก ${s.windGustsKmh})` : "";
    thParts.push(`ลม ${s.windKmh} กม./ชม.${gust}`);
    enParts.push(`wind ${s.windKmh} km/h${s.windGustsKmh != null && s.windGustsKmh > s.windKmh ? ` (gusting ${s.windGustsKmh})` : ""}`);
  }
  if (s.smallBoatBelowThreshold === false) {
    level = worseLevel(level, "alert");
    thParts.push("เกินเกณฑ์แบบจำลองสำหรับเรือเล็ก ตรวจประกาศทางการ");
    enParts.push("above small-boat model threshold; check official advisories");
  }
  if (s.ferryBelowThreshold === false) {
    level = worseLevel(level, "alert");
    thParts.push("เกินเกณฑ์แบบจำลองเรือข้ามฟาก ตรวจข้อมูลผู้ให้บริการ");
    enParts.push("above ferry model threshold; check operator advisories");
  }
  if (s.tideHeightM != null) {
    thParts.push(`แบบจำลองน้ำขึ้นลงโดยประมาณ ${s.tideHeightM} ม.${s.tideRising ? " (กำลังขึ้น)" : " (กำลังลด)"}`);
    enParts.push(`approximate harmonic tide ${s.tideHeightM} m (${s.tideRising ? "rising" : "falling"})`);
    if (s.springTide) {
      level = worseLevel(level, "watch");
      thParts.push("น้ำขึ้นเต็มที่");
      enParts.push("spring tide");
    }
  }
  if (s.peakWaveHeightNext24hM != null) {
    level = worseLevel(level, s.peakWaveHeightNext24hM >= 2.5 ? "alert" : s.peakWaveHeightNext24hM >= 1.5 ? "watch" : "good");
    thParts.push(`คาดการณ์คลื่นสูงสุด ${s.peakWaveHeightNext24hM} ม. ใน 24 ชม. ไม่มีข้อมูลน้ำทะเลหนุนจากพายุ`);
    enParts.push(`maximum forecast wave height ${s.peakWaveHeightNext24hM} m in 24 h; storm surge unavailable`);
  }

  if (!thParts.length) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีตัวเลขสถานการณ์ทะเลที่ใช้ได้",
      en: "No usable sea-state value",
    };
  }

  return {
    ...base,
    level,
    summaryTh: `คลื่นแบบจำลอง ${s.waveHeightM ?? "—"} ม. · ลม ${s.windKmh == null ? "ไม่ทราบ" : `${s.windKmh} กม./ชม.`} · น้ำขึ้นลงโดยประมาณ ${s.tideHeightM ?? "—"} ม. · ไม่มีข้อมูลน้ำทะเลหนุนจากพายุ`,
    summaryEn: `Model waves ${s.waveHeightM ?? "—"} m · wind ${s.windKmh == null ? "unknown" : `${s.windKmh} km/h`} · tide approximation ${s.tideHeightM ?? "—"} m; surge unknown`,
    figure:
      s.waveHeightM != null
        ? { th: `คลื่น ${s.waveHeightM} ม.`, en: `waves ${s.waveHeightM} m` }
        : undefined,
    ...(s.waveHeightM == null || s.windKmh == null ? {
      level: level === "good" ? "unknown" as const : level,
      levelLabel: { th: "ข้อมูลแบบจำลองไม่ครบ", en: "Incomplete forecast" },
    } : {}),
    th: `${thParts.join(" · ")} · แบบจำลองนอกชายฝั่ง ไม่ใช่การอนุญาตเดินเรือ`,
    en: `${enParts.join(" · ")} · offshore forecast, not navigation clearance`,
    observedAt: s.observedAt,
  };
}

function incidentTile(i: MayorBriefInputs): BriefTile {
  const c = i.incidents;
  const base = {
    key: "incidents" as const,
    titleTh: "เหตุการณ์ที่อยู่ระหว่างดำเนินการ",
    titleEn: "Open incidents",
    source: nameSource(c?.source) || `${SRC_INCIDENTS} · ${SRC_IFIC}`,
    observedAt: null as string | null,
    evidence: "operations" as const,
  };

  if (!c || !isBriefObservationCurrent(c.observedAt, i.now, 3)) {
    return {
      ...base,
      level: "unknown",
      th: "ไม่มีข้อมูลเหตุการณ์ที่เปิดอยู่ในขณะนี้",
      en: "No current open-incident count",
    };
  }

  if (c.coverage && c.coverage.available < c.coverage.total) {
    return { ...base, observedAt: c.observedAt,
      level: c.critical > 0 ? "critical" : c.high > 0 ? "alert" : "unknown",
      // The chip names what the number is, not a bare severity. "No data"
      // would be wrong here — some feeds answered — and a bare "Normal"
      // would be a false all-clear.
      levelLabel: { th: "ข้อมูลบางส่วน", en: "Partial coverage" },
      th: `ข้อมูลบางส่วน ${c.coverage.available}/${c.coverage.total} แหล่ง · พบ ${c.open} รายงานที่ยังเปิดอยู่ ไม่ทราบยอดรวม`,
      en: `Partial coverage ${c.coverage.available}/${c.coverage.total} sources: ${c.open} open ${c.open === 1 ? "report" : "reports"} in available feeds; total unknown`,
    };
  }

  if (c.open === 0) {
    return {
      ...base,
      level: "good",
      th: "ไม่พบรายงานที่เปิดอยู่ในแหล่งข้อมูลที่เชื่อมต่อ ไม่ใช่การยืนยันว่าทั้งเมืองปลอดเหตุ",
      en: "No open reports in connected feeds; not a citywide all-clear",
      observedAt: c.observedAt,
    };
  }

  const level = worseLevel(
    c.critical > 0 ? "critical" : c.high > 0 ? "alert" : c.flooding > 0 ? "watch" : "good",
    "good",
  );

  const severity: string[] = [];
  const severityEn: string[] = [];
  if (c.critical > 0) {
    severity.push(`วิกฤต ${c.critical}`);
    severityEn.push(`${c.critical} critical`);
  }
  if (c.high > 0) {
    severity.push(`รุนแรง ${c.high}`);
    severityEn.push(`${c.high} high`);
  }
  if (c.flooding > 0) {
    severity.push(`น้ำท่วม ${c.flooding}`);
    severityEn.push(`${c.flooding} flooding`);
  }

  const detail = severity.length ? ` · ${severity.join(" · ")}` : "";
  const detailEn = severityEn.length ? ` · ${severityEn.join(" · ")}` : "";

  return {
    ...base,
    level,
    th: `เหตุการณ์ที่ยังดำเนินการอยู่ ${c.open} รายการ${detail}`,
    en: `${c.open} ${c.open === 1 ? "incident" : "incidents"} still open${detailEn}`,
    figure: {
      th: `${c.open} รายการ${c.critical > 0 ? ` · วิกฤต ${c.critical}` : c.high > 0 ? ` · รุนแรง ${c.high}` : ""}`,
      en: `${c.open} open${c.critical > 0 ? ` · ${c.critical} critical` : c.high > 0 ? ` · ${c.high} high` : ""}`,
    },
    observedAt: c.observedAt,
  };
}

/** The four tiles, in reading order. */
export function buildTiles(i: MayorBriefInputs): BriefTile[] {
  return [airTile(i), rainTile(i), seaTile(i), incidentTile(i)];
}

// ── Shaping ────────────────────────────────────────────────────────────────

/** The incident fields the brief needs — structurally typed so the lib does
 *  not depend on the dashboard's component types. */
export interface RawIncident {
  severity: string;
  status: string;
  category: string;
  reportedAt: string;
}
/**
 * Fold raw incident records into the brief's incident input.
 *
 * "Open" means the record is not resolved. Resolved records are still counted
 * for freshness, so an incident resolved two minutes ago still proves the feed
 * is alive.
 */
export function shapeIncidents(
  items: RawIncident[],
  observedAt: string | null,
  source: string,
): IncidentBriefInput {
  const open = items.filter((i) => i.status !== "resolved");
  const bySeverity = (levels: string[]) => open.filter((i) => levels.includes(i.severity)).length;

  return {
    open: open.length,
    critical: bySeverity(["critical"]),
    high: bySeverity(["high"]),
    flooding: open.filter((i) => i.category === "flooding").length,
    observedAt,
    source,
  };
}

export interface IncidentBriefFeed {
  items: RawIncident[];
  tier: FallbackTier | "loading";
  observedAt: string | null;
  source: string;
}

export function aggregateIncidentFeeds(feeds: IncidentBriefFeed[], now = new Date()): IncidentBriefInput | null {
  const usable = feeds.filter(f => f.tier === "live" && isBriefObservationCurrent(f.observedAt, now, 3));
  if (!usable.length) return null;
  const oldest = usable.map(f => f.observedAt!).sort((a,b) => Date.parse(a)-Date.parse(b))[0];
  return { ...shapeIncidents(usable.flatMap(f => f.items), oldest, usable.map(f => f.source).join(" · ")),
    coverage: { available: usable.length, total: feeds.length } };
}

/** The fields the brief needs from an air-quality point. */
export interface RawAirPoint {
  lat: number;
  lng: number;
  station: string;
  aqi: number | null;
  pm25: number | null;
  category: string | null;
  aqiScale?: "thai-pcd" | "us-epa";
  observedAt: string;
  source: string;
}

/**
 * Choose the air reading the mayor brief should quote.
 *
 * A government station reading beats a forecast grid cell, so the brief
 * prefers a measured PCD observation. The PCD feed also carries the
 * neighbouring Rayong industrial belt, so among the stations that are
 * current we take the one nearest the town centre — a Chonburi municipality
 * is not answered by a Rayong reading.
 *
 * With no current station reading the Open-Meteo grid is used, and the tile
 * says it is a model rather than a measurement.
 */
export function distanceKm(a: [number, number], b: [number, number]): number {
  const rad = (v: number) => v * Math.PI / 180;
  const dLat = rad(b[1]-a[1]), dLon = rad(b[0]-a[0]);
  const h = Math.sin(dLat/2)**2 + Math.cos(rad(a[1]))*Math.cos(rad(b[1]))*Math.sin(dLon/2)**2;
  return 6371 * 2 * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function selectBriefAir(
  stations: RawAirPoint[],
  grid: RawAirPoint | null,
  center: [number, number],
  now = new Date(),
  maxAgeMinutes = 90,
): AirBriefInput | null {
  const current = stations.filter((s) => {
    const age = now.getTime() - Date.parse(s.observedAt);
    return s.pm25 != null && Number.isFinite(age) && age >= -300_000 && age <= maxAgeMinutes * 60_000;
  });

  if (current.length) {
    const [cLng, cLat] = center;
    const nearest = current.reduce((best, s) => {
      const d = (s.lng - cLng) ** 2 + (s.lat - cLat) ** 2;
      const bd = (best.lng - cLng) ** 2 + (best.lat - cLat) ** 2;
      return d < bd ? s : best;
    });
    return {
      aqi: nearest.aqi,
      pm25: nearest.pm25,
      category: nearest.category,
      station: nearest.station,
      distanceKm: distanceKm(center, [nearest.lng, nearest.lat]),
      observedAt: nearest.observedAt,
      source: nearest.source,
      kind: "measured",
      scale: nearest.aqiScale ?? "thai-pcd",
    };
  }

  if (!grid) return null;
  return {
    aqi: grid.aqi,
    pm25: grid.pm25,
    category: grid.category,
    station: grid.station,
    observedAt: grid.observedAt,
    source: grid.source,
    kind: "model",
    scale: grid.aqiScale ?? "us-epa",
  };
}

// ── Actions and data integrity ─────────────────────────────────────────────

export interface BriefAction {
  id: string;
  th: string;
  en: string;
}

/**
 * "Points to check next" — what a non-normal tile implies should be verified.
 * These are checks, not claims: the brief never asserts an action was taken.
 *
 * Only `alert` and `critical` raise a check. A `watch` tile stays visible with
 * its own level label, but a routine "moderate" AQI must not put an action in
 * front of the mayor every day — a section that always has something in it
 * stops being read.
 */
export function buildActions(tiles: BriefTile[], feeds: FeedHealthInput): BriefAction[] {
  const actions: BriefAction[] = [];

  for (const tile of tiles) {
    if (tile.level !== "alert" && tile.level !== "critical") continue;
    switch (tile.key) {
      case "air":
        actions.push({
          id: "air",
          th: "คุณภาพอากาศ: ติดตามค่าฝุ่น PM2.5 ในพื้นที่และประสานหน่วยงานควบคุมการเผา",
          en: "Air quality: track local PM2.5 and coordinate with open-burning control",
        });
        break;
      case "rain":
        if (tile.driver === "storage") {
          // A dry reservoir is not a flood. The check has to name the check.
          actions.push({
            id: "rain-storage",
            th: "กักเก็บน้ำ: ตรวจสอบการปล่อยน้ำและปริมาณน้ำที่ใช้ได้ของอ่างเก็บน้ำที่เหลือน้ำน้อยที่สุด และประสานกรมชลประทาน",
            en: "Water storage: check the release and the usable volume of the reservoir with the least water left, and coordinate with the Royal Irrigation Department",
          });
        } else {
          actions.push({
            id: "rain",
            th: "คาดการณ์ฝน: ตรวจข้อมูลเรดาร์ สถานีฝน และรายงานภาคสนามก่อนพิจารณาแจ้งเตือน",
            en: "Rain forecast: cross-check radar, gauges and field reports before considering public warnings",
          });
        }
        break;
      case "sea":
        actions.push({
          id: "sea",
          th: "สถานการณ์ทะเล: ประสานชาวเรือและหน่วยกู้ภัยชายฝั่ง และพิจารณางดกิจกรรมเรือเล็ก",
          en: "Sea state: liaise with fishermen and coastal rescue, and consider suspending small-boat activity",
        });
        break;
      case "incidents":
        actions.push({
          id: "incidents",
          th: "เหตุการณ์: จัดสรรกำลังเจ้าหน้าที่ตามระดับความรุนแรง และปิดเหตุการณ์เร่งด่วน",
          en: "Incidents: allocate crews by severity and close the urgent ones",
        });
        break;
    }
  }

  if (feeds.inputs ? feeds.inputs.some(f => ["cache", "database", "scenario", "unavailable", "loading"].includes(f.tier)) : feeds.degraded > 0 || feeds.down > 0) {
    actions.push({
      id: "feeds",
      th: "ความสมบูรณ์ของข้อมูล: ตรวจสอบแหล่งข้อมูลที่ทำงานไม่เต็มที่หรือใช้งานไม่ได้ก่อนใช้ตัวเลขเหล่านี้ตัดสินใจ",
      en: "Data integrity: check the degraded or down feeds before using these numbers for decisions",
    });
  }

  return actions;
}

/** How much of the dashboard's own instrumentation is actually working. */
export function feedHealthSentence(f: FeedHealthInput): { th: string; en: string } {
  const loading = f.loading ?? 0;
  if (f.scope === "brief") return {
    th: `ข้อมูลสรุป ${f.live}/${f.total} แหล่งตอบปัจจุบัน · แคช/อ้างอิง ${f.degraded} · ตัวอย่าง ${f.scenario ?? 0} · ไม่มีข้อมูล ${f.down} · รอ ${loading}`,
    en: `Brief inputs: ${f.live}/${f.total} current · ${f.degraded} cached/reference · ${f.scenario ?? 0} scenario · ${f.down} unavailable · ${loading} loading`,
  };
  const th = `แหล่งข้อมูล ${f.live} จาก ${f.total} แหล่งใช้งานได้ · ทำงานไม่เต็มที่ ${f.degraded} · ใช้งานไม่ได้ ${f.down}${loading > 0 ? ` · กำลังโหลด ${loading}` : ""}`;
  const en = `${f.live} of ${f.total} feeds live · ${f.degraded} degraded · ${f.down} down${loading > 0 ? ` · ${loading} loading` : ""}`;
  return { th, en };
}

// ── Forwardable text ───────────────────────────────────────────────────────

/**
 * The authority behind each tile, named once.
 *
 * A tile can draw on two feeds (the sea state reads the wave model and the
 * tide table), so the tiles' own source strings overlap. Listing whole tile
 * sources printed "Open-Meteo" twice in one footer. The footer names
 * authorities, so it flattens the list and keeps each one.
 */
function sourceList(tiles: BriefTile[]): string {
  const names = tiles.flatMap((t) => t.source.split(" · ")).map((s) => s.trim()).filter(Boolean);
  return [...new Set(names)].join(" · ");
}

/**
 * Plain text for a LINE group: dated, one line per hazard, the checks to run,
 * the sources, and the link. No markdown — LINE shows it literally.
 */
export function lineText(
  tiles: BriefTile[],
  actions: BriefAction[],
  now: Date,
  url: string,
  feeds?: FeedHealthInput,
): string {
  const lines: string[] = [
    `สรุปสถานการณ์เทศบาลเมืองชลบุรี ${thaiDate(now.toISOString())} เวลา ${ictTime(now)}`,
  ];

  for (const tile of tiles) {
    const stamp = Number.isFinite(Date.parse(tile.observedAt ?? ""))
      ? new Date(tile.observedAt!).toLocaleString("th-TH", {
          timeZone: "Asia/Bangkok",
          dateStyle: "short",
          timeStyle: "short",
        })
      : "ไม่ทราบ";
    lines.push(`${MARK[tile.level]} ${tile.titleTh}: ${tile.th}\n  เวลาอ้างอิง: ${stamp}`);
  }

  if (actions.length) {
    lines.push("ประเด็นที่ควรตรวจสอบต่อ:");
    for (const action of actions) lines.push(`• ${action.th}`);
  }

  if (feeds) {
    const health = feedHealthSentence(feeds);
    lines.push(`สถานะระบบข้อมูล: ${health.th}`);
  }

  lines.push(`ที่มา: ${sourceList(tiles)}`);
  lines.push(`ดูต่อ: ${url}`);

  return lines.join("\n");
}

/** The same brief in English, for staff or partners who forward it outside Thailand. */
export function englishText(
  tiles: BriefTile[],
  actions: BriefAction[],
  now: Date,
  url: string,
  feeds?: FeedHealthInput,
): string {
  const lines: string[] = [
    `Chonburi Town Municipality — situation summary, ${now.toLocaleDateString("en-GB", { timeZone: "Asia/Bangkok", dateStyle: "long" })} ${ictTime(now)}`,
  ];

  for (const tile of tiles) {
    const stamp = Number.isFinite(Date.parse(tile.observedAt ?? ""))
      ? new Date(tile.observedAt!).toLocaleString("en-GB", {
          timeZone: "Asia/Bangkok",
          dateStyle: "short",
          timeStyle: "short",
        })
      : "unknown";
    lines.push(`${MARK[tile.level]} ${tile.titleEn}: ${tile.en}\n  Reference time: ${stamp}`);
  }

  if (actions.length) {
    lines.push("Points to check next:");
    for (const action of actions) lines.push(`• ${action.en}`);
  }

  if (feeds) {
    lines.push(`Data status: ${feedHealthSentence(feeds).en}`);
  }

  lines.push(`Sources: ${sourceList(tiles)}`);
  lines.push(`Read more: ${url}`);

  return lines.join("\n");
}
